import React, { useState } from "react";
import styles from "./ListToWebsiteModal.module.css";
import Select from "react-select";
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  CircularProgress,
} from "@mui/material";

const AddProductModal = ({
  onClose,
  handleListData,
  postLoader,
  marketPlaceId,
}) => {
  const [categories, setCategories] = useState([]);
  // Plans available to publish. Each becomes ONE child page on the website
  // (e.g. "Google Workspace Starter"), which is what a visitor actually shops
  // for — the old listing published one page per purchase TYPE (Fresh/Renew).
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState({});
  // What is already published for this product, so an existing listing opens
  // showing its real state rather than a fresh default.
  const [listing, setListing] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [isLoaded, setIsLoaded] = React.useState(false);

  React.useEffect(() => {
    async function fetchCategoryData() {
      setIsLoaded(false);
      try {
        fetch(`${process.env.REACT_APP_SERVER_URL}/get_all_categories`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "GET",
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else {
              console.log("some error");
              setIsLoaded(true);
              return response.json();
            }
          })
          .then((data) => {
            setCategories(data.data);
            setIsLoaded(true);
          })
          .catch((error) => console.log(error));
      } catch (error) {
        console.log(error);
        setIsLoaded(true);
      }
    }
    async function fetchSubCategoryData() {
      try {
        fetch(`${process.env.REACT_APP_SERVER_URL}/get_all_subcategories`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "GET",
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else {
              setIsLoaded(true);
              console.log("some error");
              return response.json();
            }
          })
          .then((data) => {
            setSubCategories(data.data);
            setIsLoaded(true);
          })
          .catch((error) => console.log(error));
      } catch (error) {
        console.log(error);
        setIsLoaded(true);
      }
    }
    async function fetchPlans() {
      if (!marketPlaceId) return;
      setPlansLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/admin/marketplace/listable-plans`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            credentials: "include",
            method: "POST",
            body: JSON.stringify({ id: marketPlaceId }),
          },
        );
        const body = await res.json();
        if (body?.success === false) {
          alert(body.msg || "Could not load plans");
          setPlans([]);
          return;
        }
        const list = body?.data?.plans || [];
        const state = body?.data?.listing || { listed: false };
        setPlans(list);
        setListing({ ...state, quote_only: !!body?.data?.quote_only });

        // An EXISTING listing opens reflecting what is actually live: published
        // plans ticked, the rest not. Pre-ticking everything would mean an admin
        // who re-opens the modal and submits silently republishes plans they had
        // deliberately left off.
        //
        // A FIRST listing (or one whose pages were built by the old type-based
        // engine, so none of them match a plan) has no selection to restore, so
        // fall back to every priced plan — the sensible default.
        const restore = state.listed && state.recognised;
        const preselected = {};
        list.forEach((p) => {
          if (!p.has_price) return;
          preselected[p.plan_name] = restore ? !!p.published : true;
        });
        setSelectedPlans(preselected);
      } catch (error) {
        console.log(error);
      } finally {
        setPlansLoading(false);
      }
    }
    fetchCategoryData();
    fetchSubCategoryData();
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UI state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  const [openCategoryDrawer, setOpenCategoryDrawer] = useState(false);
  const [openSubCategoryDrawer, setOpenSubCategoryDrawer] = useState(false);

  // Drawer inputs
  const [newCategory, setNewCategory] = useState("");
  const [newSubCategory, setNewSubCategory] = useState("");
  const [newSubCategoryCat, setNewSubCategoryCat] = useState(null);

  // Categories, sub-categories and the listing state arrive from three separate
  // requests, so the preselect has to wait until the sub-category the product is
  // filed under is actually resolvable. Runs once — after that the admin's own
  // choices stand.
  const [prefilled, setPrefilled] = useState(false);
  React.useEffect(() => {
    if (prefilled) return;
    if (!listing?.listed || listing.sub_category_id == null) return;
    if (!subCategories?.length || !categories?.length) return;

    const sub = subCategories.find((sc) => sc.id === listing.sub_category_id);
    if (!sub) return;
    const cat = categories.find((c) => c.id === sub.category_id) || null;
    setSelectedCategory(cat);
    setSelectedSubCategory(sub);
    setPrefilled(true);
  }, [listing, subCategories, categories, prefilled]);

  const chosenPriceIds = plans
    .filter((p) => p.has_price && selectedPlans[p.plan_name])
    .flatMap((p) => p.price_ids);

  // Live pages the admin has just unticked. Submitting deletes them, so say so
  // before they click rather than after.
  const willRemove = plans
    .filter((p) => p.published && !selectedPlans[p.plan_name])
    .map((p) => p.plan_name);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSubCategory) {
      alert("Pick a sub category first");
      return;
    }
    // A quote-only product (cloud/consumption billing) has no plans to tick —
    // it is published for its description and Want-to-buy button alone.
    if (!listing?.quote_only && plans.length && !chosenPriceIds.length) {
      alert("Select at least one plan to list");
      return;
    }
    handleListData(selectedSubCategory.id, chosenPriceIds);
  };

  const togglePlan = (name) =>
    setSelectedPlans((prev) => ({ ...prev, [name]: !prev[name] }));

  const priceSummary = (p) => {
    if (!p.has_price) return "no price set — cannot be listed";
    const seen = new Set();
    return p.prices
      .map((pr) => {
        const sym = pr.currency === "INR" ? "\u20b9" : "$";
        const amount = pr.min === pr.max ? `${pr.min}` : `${pr.min}-${pr.max}`;
        return `${sym}${amount}/${pr.interval}`;
      })
      .filter((label) => {
        if (seen.has(label)) return false;
        seen.add(label);
        return true;
      })
      .join("  \u00b7  ");
  };

  const categoryOptions = categories?.map((c) => ({
    value: c.id,
    label: c.name,
  }));
  const subCategoryOptions = subCategories
    ?.filter((sc) => selectedCategory && sc.category_id === selectedCategory.id)
    ?.map((sc) => ({ value: sc.id, label: sc.name }));

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    const res = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/add_category`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: name }),
      },
    );

    if (res.ok) {
      alert("Category added successfully");
      onClose();
    } else {
      const err = await res.json();
      alert("Error: " + (err.message || "Failed to add product"));
    }
    setNewCategory("");
    setOpenCategoryDrawer(false);
  };

  const addSubCategory = async () => {
    const name = newSubCategory.trim();
    if (!name || !newSubCategoryCat) return;
    const res = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/add_subcategory`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: name,
          category_id: selectedCategory,
        }),
      },
    );

    if (res.ok) {
      alert("Sub Category added successfully");
      onClose();
    } else {
      const err = await res.json();
      alert("Error: " + (err.message || "Failed to add product"));
    }
    setNewSubCategory("");
    setNewSubCategoryCat(null);
    setOpenSubCategoryDrawer(false);
  };

  return (
    <>
      {!isLoaded && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}
      {isLoaded && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>
              {listing?.listed ? "Update Website Listing" : "List to Website"}
            </h2>
            {listing?.listed && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Already live at <strong>{listing.url}</strong> with{" "}
                {listing.published_count} plan page(s). Submitting replaces what
                is published — unticked plans are removed from the website.
              </Typography>
            )}
            {listing?.listed && !listing.recognised && (
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 1, color: "#b45309" }}
              >
                The pages currently live were built the old way (named after
                purchase types, not plans). Pick the plans below and submit to
                rebuild this listing properly.
              </Typography>
            )}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "2%" }}>
                <Select
                  options={categoryOptions}
                  value={
                    selectedCategory
                      ? {
                          value: selectedCategory.id,
                          label: selectedCategory.name,
                        }
                      : null
                  }
                  onChange={(option) => {
                    const cat =
                      categories.find((c) => c.id === option.value) || null;
                    setSelectedCategory(cat);
                    setSelectedSubCategory(null); // reset subcat
                  }}
                  placeholder="Select Category"
                />
                <button
                  style={{ marginTop: "2%" }}
                  type="button"
                  onClick={() => setOpenCategoryDrawer(true)}
                >
                  + Add
                </button>
              </div>

              {/* SubCategory Select + Add */}
              <div style={{ marginBottom: "2%" }}>
                <Select
                  options={subCategoryOptions}
                  value={
                    selectedSubCategory
                      ? {
                          value: selectedSubCategory.id,
                          label: selectedSubCategory.name,
                        }
                      : null
                  }
                  onChange={(option) => {
                    const sub =
                      subCategories.find((s) => s.id === option.value) || null;
                    setSelectedSubCategory(sub);
                  }}
                  placeholder="Select Sub Category"
                  className="flex-1"
                  isDisabled={!selectedCategory}
                  noOptionsMessage={() =>
                    selectedCategory
                      ? "No subcategories"
                      : "Select a category first"
                  }
                />
                <button
                  type="button"
                  style={{ marginTop: "2%" }}
                  onClick={() => {
                    // Default the drawer's category to the currently selected one
                    if (selectedCategory)
                      setNewSubCategoryCat(selectedCategory);
                    setOpenSubCategoryDrawer(true);
                  }}
                >
                  + Add
                </button>
              </div>

              {/* Plans to publish. Each ticked plan becomes one page on the
                  website, priced from the cheapest purchase type for each
                  billing period. */}
              <div style={{ marginBottom: "2%" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Plans to list on the website
                </Typography>
                {plansLoading && <CircularProgress size={20} />}
                {!plansLoading && plans.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No priced plans — this will be published as a quote-only
                    page: description, advantages and a Want-to-buy button, with
                    no pricing section.
                  </Typography>
                )}
                {!plansLoading && plans.length > 0 && (
                  <Box
                    sx={{
                      maxHeight: 220,
                      overflowY: "auto",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {plans.map((p) => (
                      <Box
                        key={p.plan_name}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          py: 0.5,
                          opacity: p.has_price ? 1 : 0.5,
                        }}
                      >
                        <input
                          type="checkbox"
                          disabled={!p.has_price}
                          checked={!!selectedPlans[p.plan_name]}
                          onChange={() => togglePlan(p.plan_name)}
                          style={{ marginTop: 4 }}
                        />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {p.plan_name}
                            {p.published && (
                              <span
                                style={{
                                  marginLeft: 6,
                                  fontSize: 11,
                                  fontWeight: 500,
                                  color: "#0f766e",
                                }}
                              >
                                live
                              </span>
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {priceSummary(p)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {p.url}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {chosenPriceIds.length
                    ? `${plans.filter((p) => selectedPlans[p.plan_name] && p.has_price).length} plan page(s) will be published`
                    : "Nothing selected"}
                </Typography>
                {willRemove.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.5, color: "#b45309" }}
                  >
                    Will be REMOVED from the website: {willRemove.join(", ")}
                  </Typography>
                )}
              </div>

              <div className={styles.buttonRow}>
                <button type="submit">
                  {postLoader && (
                    <CircularProgress size={25} sx={{ color: "white" }} />
                  )}
                  {!postLoader && "Submit"}
                </button>
                <button type="button" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
          <Drawer
            anchor="right"
            open={openCategoryDrawer}
            onClose={() => setOpenCategoryDrawer(false)}
            sx={{ width: "50%" }}
          >
            <Box sx={{ width: 340, p: 2 }} role="presentation">
              <Typography variant="h6" sx={{ mb: 1 }}>
                Add Category
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField
                label="Category Name"
                fullWidth
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                autoFocus
              />
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button variant="contained" onClick={addCategory}>
                  Save
                </Button>
                <Button onClick={() => setOpenCategoryDrawer(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Drawer>

          {/* Drawer: Add Sub Category */}
          <Drawer
            anchor="right"
            open={openSubCategoryDrawer}
            onClose={() => setOpenSubCategoryDrawer(false)}
            sx={{ width: "50%" }}
          >
            <Box sx={{ width: 340, p: 2 }} role="presentation">
              <Typography variant="h6" sx={{ mb: 1 }}>
                Add Sub Category
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <TextField
                label="Sub Category Name"
                fullWidth
                required
                value={newSubCategory}
                onChange={(e) => setNewSubCategory(e.target.value)}
              />

              <div className="mt-3">
                <Select
                  options={categoryOptions}
                  value={
                    newSubCategoryCat
                      ? {
                          value: newSubCategoryCat.id,
                          label: newSubCategoryCat.name,
                        }
                      : null
                  }
                  onChange={(option) => {
                    const cat =
                      categories.find((c) => c.id === option.value) || null;
                    setNewSubCategoryCat(cat);
                  }}
                  required
                  placeholder="Select Category"
                />
              </div>

              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button variant="contained" onClick={addSubCategory}>
                  Save
                </Button>
                <Button onClick={() => setOpenSubCategoryDrawer(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Drawer>
        </div>
      )}
    </>
  );
};

export default AddProductModal;
